import { takeEvery, put, select } from 'redux-saga/effects';
import callRequestSaga from '~/sagas/utils/request-saga';
import AccessGroupsAssetsAPI from '~/api/access-groups-assets-api';
import BaseException from '~/utils/fetch-wrapper/errors/BaseException';
import type { AppState } from '~/reducers';
import type { patchAccessGroupsAssetsAction } from './actions';
import AccessGroupsAssetsConstants from './constants';
import { createBatches } from './utils';

const ASSETS_BATCH_SIZE = process.env.ASSETS_BATCH_SIZE
  ? parseInt(process.env.ASSETS_BATCH_SIZE)
  : 10;

export const isPatchQueueRunning = (state: AppState) => state.accessGroupsAssets.isLoading;

export function* patchAccessGroupsAssets({
  payload,
}: ReturnType<typeof patchAccessGroupsAssetsAction>) {
  const batches = createBatches(payload, ASSETS_BATCH_SIZE);

  yield put({
    type: AccessGroupsAssetsConstants.PATCH_ACCESS_GROUPS_ASSETS_INIT,
    payload,
  });

  const failedLastAccessGroupAssetsIds: string[] = [];

  for (const batch of batches) {
    const assetsAdded: string[] = [];
    const assetsDeleted: string[] = [];
    const isRunning = yield select(isPatchQueueRunning);
    if (!isRunning) {
      continue;
    }

    const response = yield* callRequestSaga(
      AccessGroupsAssetsAPI.patchAccessGroupsAssets,
      {
        request: AccessGroupsAssetsConstants.PATCH_ACCESS_GROUPS_ASSETS_REQUEST,
        failure: AccessGroupsAssetsConstants.PATCH_ACCESS_GROUPS_ASSETS_REQUEST_ERROR,
        payload: batch,
      },
      batch.accessGroupId,
      batch.assetsToBeAdded,
      batch.assetsToBeDeleted
    );

    yield put({
      type: AccessGroupsAssetsConstants.PATCH_ACCESS_GROUPS_ASSETS_RESPONSE,
      payload: {
        payload: batch,
        response: response,
      },
    });

    if (response instanceof BaseException) {
      //
    } else {
      if (response instanceof Array && response.length > 0) {
        // Scan for last-access-group errors
        response.forEach(
          (item) =>
            item.failures instanceof Array &&
            item.failures.includes('LAST_ACCESS_GROUP') &&
            failedLastAccessGroupAssetsIds.push(item.assetId)
        );

        response.forEach(
          (item) =>
            item.success &&
            item.assetId.length > 0 &&
            batch.assetsToBeAdded.includes(item.assetId) &&
            assetsAdded.push(item.assetId)
        );
        response.forEach(
          (item) =>
            item.success &&
            item.assetId.length > 0 &&
            batch.assetsToBeDeleted.includes(item.assetId) &&
            assetsDeleted.push(item.assetId)
        );
      }
    }

    yield put({
      type: AccessGroupsAssetsConstants.PATCH_ACCESS_GROUPS_ASSETS_REQUEST_ASSETS_ADDED,
      payload: assetsAdded,
    });

    yield put({
      type: AccessGroupsAssetsConstants.PATCH_ACCESS_GROUPS_ASSETS_REQUEST_ASSETS_DELETED,
      payload: assetsDeleted,
    });
  }

  yield put({
    type: AccessGroupsAssetsConstants.PATCH_ACCESS_GROUPS_ASSETS_REQUEST_SUCCESS,
    payload: {
      payload: payload,
      failedLastAccessGroup: failedLastAccessGroupAssetsIds,
    },
  });
}

export function* patchAccessGroupsAssetsSaga() {
  yield takeEvery(AccessGroupsAssetsConstants.PATCH_ACCESS_GROUPS_ASSETS, patchAccessGroupsAssets);
}

export default [patchAccessGroupsAssetsSaga];
